import { useState, useEffect, useRef, useCallback, useLayoutEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { useParams } from 'react-router';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import Skeleton from '../../../components/ui/skeleton';
import Heading from '../../../components/Heading';
import usePromotion from '../../../stores/usePromo';
import PromoCard from './PromoCard';
import { LanguageContext } from '../../../contexts/LanguageContext';

const calculateInitialSlidesPerView = () => {
  const width = window.innerWidth;
  if (width < 300) return 2;
  if (width < 400) return 2;
  if (width < 620) return 3;
  if (width < 700) return 3;
  if (width < 980) return 4;
  return 5;
};

const PROMO_CARD_GAP = 8;

export function PromoSlider({ className = '' }) {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext) || { language: 'id' };
  const lang = language === 'id';
  const {
    promotionBySrv,
    hasFetched,
    fetchLoading,
    fetchPromotionByServiceId,
    pageBySrv,
    hasMoreBySrv,
    isLoadingNextPage,
    cache,
  } = usePromotion();
  const [slidesPerView, setSlidesPerView] = useState(calculateInitialSlidesPerView);
  const [cardWidth, setCardWidth] = useState(200);
  const containerRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<any>(null);
  const { id } = useParams();
  const serviceId = Number(id);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const filteredPromotions = useMemo(
    () => promotionBySrv.filter((promotion) => promotion.service_id === serviceId),
    [promotionBySrv, serviceId],
  );

  useEffect(() => {
    const cacheKey = `${serviceId}-1`;
    if (isInitialLoad && !cache[cacheKey]) {
      fetchPromotionByServiceId(1, false, serviceId).then(() => setIsInitialLoad(false));
    } else {
      setIsInitialLoad(false);
    }
  }, [fetchPromotionByServiceId, serviceId, cache, isInitialLoad]);

  const updateLayout = useCallback(() => {
    const width = window.innerWidth;
    let newCardWidth = 250;
    if (width < 370) newCardWidth = 158;
    else if (width < 640) newCardWidth = 150;
    else if (width < 1024) newCardWidth = 180;

    setCardWidth(newCardWidth);

    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const calculatedSlides = containerWidth / (newCardWidth + PROMO_CARD_GAP);
      let slidesToSet = Math.floor(calculatedSlides) || 2;
      if (width < 300) slidesToSet = 1.6;
      else if (width < 400) slidesToSet = 2;
      else if (width < 620) slidesToSet = 2.5;
      else if (width < 700) slidesToSet = 3;
      else if (width < 980) slidesToSet = 3.5;
      else if (width > 980) slidesToSet = 5;
      else slidesToSet = Math.floor(calculatedSlides);

      setSlidesPerView(slidesToSet);
      if (swiperRef.current?.swiper) swiperRef.current.swiper.update();
    }
  }, []);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout, filteredPromotions]);

  useEffect(() => {
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [updateLayout]);

  useEffect(() => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.update();
    }
  }, [filteredPromotions, slidesPerView]);

  const handleReachEndInner = useCallback(() => {
    const cacheKey = `${serviceId}-${pageBySrv + 1}`;
    if (
      filteredPromotions.length >= slidesPerView &&
      hasMoreBySrv &&
      !fetchLoading &&
      !isLoadingNextPage &&
      !cache[cacheKey]
    ) {
      console.log('Fetching next page (debounced):', pageBySrv + 1);
      fetchPromotionByServiceId(pageBySrv + 1, false, serviceId);
    }
  }, [
    filteredPromotions.length,
    slidesPerView,
    hasMoreBySrv,
    fetchLoading,
    isLoadingNextPage,
    pageBySrv,
    serviceId,
    cache,
    fetchPromotionByServiceId,
  ]);

  const handleReachEnd = debounce(handleReachEndInner, 200);

  const skeletonSlides = Array.from({ length: 10 }, (_, i) => (
    <SwiperSlide key={`skeleton-${i}`} style={{ width: cardWidth }}>
      <Skeleton className={`shadow-md h-52 sm:h-64 w-[${cardWidth}px]`} />
    </SwiperSlide>
  ));

  const loadingNextPageSkeleton = (
    <SwiperSlide key="loading-next-page" style={{ width: cardWidth }}>
      <Skeleton className={`shadow-md h-52 sm:h-64 w-[${cardWidth}px]`} />
    </SwiperSlide>
  );

  const promoSlides = filteredPromotions.map((promotion) => (
    <SwiperSlide key={promotion.promo_id} className="pb-12" style={{ width: cardWidth }}>
      <PromoCard
        service={lang ? promotion.service.name_srv_id : promotion.service.name_srv_en || promotion.service.name_srv_id}
        name={lang ? promotion.name_prm : promotion.name_prm_en || promotion.name_prm}
        price={promotion.price_prm}
        imageUrl={promotion.image_prm || '/assets/no-data-image.svg'}
        className="w-full"
      />
    </SwiperSlide>
  ));

  if (!fetchLoading && filteredPromotions.length === 0) return null;

  return (
    <section
      ref={containerRef}
      className={`${className} relative flex flex-col sm:text-md items-center gap-4 overflow-hidden mt-4 md:mt-8`}
    >
      <div className="flex flex-col gap-1 text-center mb-4 sm:text-base text-sm">
        <Heading>{t('navigation.deals')}</Heading>
        <p>{t('tagline.deals')}</p>
      </div>
      <div className="relative w-[90vw] max-w-screen-xl">
        <Swiper
          modules={[Pagination]}
          pagination={{ clickable: true, dynamicBullets: true, dynamicMainBullets: 2 }}
          centerInsufficientSlides
          slidesPerView={slidesPerView}
          spaceBetween={PROMO_CARD_GAP}
          onReachEnd={handleReachEnd}
        >
          {fetchLoading && filteredPromotions.length === 0
            ? skeletonSlides
            : [...promoSlides, ...(isLoadingNextPage ? [loadingNextPageSkeleton] : [])]}
        </Swiper>
      </div>
    </section>
  );
}
